import { useCallback } from 'react';
import { useRouter } from 'next/router';
import { removeQueryParams, addQueryParams } from '@framework/router/nextRouter';
import { getDefaultCorporateSegments } from '@/creditflowresearch/application/helpers/getCorporatePageDefaultParams';
import { getQueryParamAsString } from '@/creditflowresearch/ui/common/utils/query/parseQueryParams';

type TQuery = Record<string, string | string[] | undefined>;

interface IUseCorporateMarketFilterToggleResult {
  handleFilterToggle: (searchQuery: string) => void,
}

const withResolvedSegments = (
  nextQuery: Record<string, string | string[]>,
  currentQuery: TQuery,
  products: string,
  markets: string,
): Record<string, string | string[]> => {
  if (!markets) {
    return nextQuery;
  }

  const keepsSelection = products === getQueryParamAsString(currentQuery.products)
    && markets === getQueryParamAsString(currentQuery.markets);

  if (keepsSelection && getQueryParamAsString(nextQuery.segments)) {
    return nextQuery;
  }

  return { ...nextQuery, segments: getDefaultCorporateSegments(products, markets) };
};

export const useCorporateMarketFilterToggle = (): IUseCorporateMarketFilterToggleResult => {
  const router = useRouter();
  const { query } = router;

  const handleFilterToggle = useCallback((searchQuery: string): void => {
    const params = new URLSearchParams(searchQuery);
    const mergedQuery = addQueryParams(query, Object.fromEntries(params)) as Record<string, string | string[]>;

    const productsValue = getQueryParamAsString(mergedQuery.products);
    const marketsValue = getQueryParamAsString(mergedQuery.markets);

    const queryWithValidSegments = withResolvedSegments(mergedQuery, query, productsValue, marketsValue);
    const finalQuery = removeQueryParams(queryWithValidSegments, ['routeType', 'articleType', 'productType', 'industry']);

    router.push({ query: finalQuery }, undefined, { shallow: true });
  }, [query, router]);

  return {
    handleFilterToggle,
  };
};
